import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Indicator,
  Popover,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle, IconBell, IconChecks } from "@tabler/icons-react";
import { notificationApi } from "apis/notificationApi";
import { NotificationResponseType } from "constants/types/notification";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePopoverClickOutside } from "utils/hooks/ui/usePopoverClickOutside";
import { useNavigate } from "react-router-dom";
import { getStoredAccessToken } from "services/notificationSocket";
import { useNotificationSocket } from "utils/hooks/request/useNotificationSocket";
import {
  useNotificationUnreadCount,
  useNotifications,
} from "utils/hooks/request/useNotifications";
import useNotification from "utils/hooks/ui/useNotification";
import {
  NOTIFICATION_INITIAL_LIMIT,
  NOTIFICATION_LIMIT_STEP,
  NotificationTab,
  resolveNotificationRoute,
} from "../../config";
import { Loading } from "components";
import { NotificationEmptyState } from "./components/NotificationEmptyState/NotificationEmptyState";
import { NotificationItem } from "./components/NotificationItem/NotificationItem";
import { NotificationListSkeleton } from "./components/NotificationListSkeleton/NotificationListSkeleton";

export const NotificationBell: React.FC = () => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>("all");
  const [limits, setLimits] = useState<Record<NotificationTab, number>>({
    all: NOTIFICATION_INITIAL_LIMIT,
    unread: NOTIFICATION_INITIAL_LIMIT,
  });
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [markAllLoading, setMarkAllLoading] = useState(false);

  const viewportRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { triggerRef, dropdownRef } = usePopoverClickOutside(opened, close);

  const navigate = useNavigate();
  const { notification } = useNotification();
  const notificationRef = useRef(notification);
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    getStoredAccessToken(),
  );
  const { count, mutate: mutateCount } = useNotificationUnreadCount();

  const {
    notifications: allNotifs,
    pagination: allPagination,
    error: allError,
    isLoading: allLoading,
    isValidating: allValidating,
    mutate: mutateAllList,
  } = useNotifications(
    { page: 1, limit: limits.all },
    opened && activeTab === "all",
  );

  const {
    notifications: unreadNotifs,
    pagination: unreadPagination,
    error: unreadError,
    isLoading: unreadLoading,
    isValidating: unreadValidating,
    mutate: mutateUnreadList,
  } = useNotifications(
    { page: 1, limit: limits.unread, isRead: false },
    opened && activeTab === "unread",
  );

  const notifications = activeTab === "all" ? allNotifs : unreadNotifs;
  const pagination = activeTab === "all" ? allPagination : unreadPagination;
  const listError = activeTab === "all" ? allError : unreadError;
  const listLoading = activeTab === "all" ? allLoading : unreadLoading;
  const isValidating = activeTab === "all" ? allValidating : unreadValidating;
  const mutateList = activeTab === "all" ? mutateAllList : mutateUnreadList;

  const canLoadMore =
    !isValidating &&
    notifications !== undefined &&
    pagination !== undefined &&
    notifications.length < pagination.total;

  // Dùng IntersectionObserver thay vì onScrollPositionChange để tránh reset scroll position
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const viewport = viewportRef.current;
    if (!sentinel || !viewport) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && canLoadMore) {
          setLimits((prev) => ({
            ...prev,
            [activeTab]: prev[activeTab] + NOTIFICATION_LIMIT_STEP,
          }));
        }
      },
      { root: viewport, threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canLoadMore, activeTab]);

  const revalidateAll = useCallback(() => {
    mutateAllList();
    mutateUnreadList();
    mutateCount();
  }, [mutateAllList, mutateUnreadList, mutateCount]);

  useEffect(() => {
    notificationRef.current = notification;
  }, [notification]);

  useEffect(() => {
    const syncAccessToken = () => setAccessToken(getStoredAccessToken());

    window.addEventListener("auth:token-refreshed", syncAccessToken);
    window.addEventListener("auth:logout", syncAccessToken);
    window.addEventListener("storage", syncAccessToken);

    return () => {
      window.removeEventListener("auth:token-refreshed", syncAccessToken);
      window.removeEventListener("auth:logout", syncAccessToken);
      window.removeEventListener("storage", syncAccessToken);
    };
  }, []);

  const handleSocketToast = useCallback(
    (message: string, type: "success" | "error" | "info" = "info") => {
      if (type === "success") {
        notificationRef.current.success({ title: message, autoClose: 5000 });
        return;
      }

      if (type === "error") {
        notificationRef.current.error({ title: message, autoClose: 7000 });
        return;
      }

      notificationRef.current.primary({ title: message, autoClose: 5000 });
    },
    [],
  );

  useNotificationSocket({
    accessToken,
    onRealtimeNotification: revalidateAll,
    onToast: handleSocketToast,
  });

  const badgeLabel =
    count !== undefined && count > 0
      ? count > 99
        ? "99+"
        : String(count)
      : undefined;

  const markRead = async (id: string) => {
    mutateCount(
      (cur: any) =>
        cur ? { ...cur, data: Math.max(0, (cur.data ?? 1) - 1) } : cur,
      { revalidate: false },
    );
    try {
      await notificationApi.markOneRead(id);
      revalidateAll();
    } catch {
      mutateCount();
      notification.error({ title: "Lỗi", message: "Không thể đánh dấu đã đọc" });
      throw new Error("mark-read-failed");
    }
  };

  const handleItemClick = async (item: NotificationResponseType) => {
    if (loadingId) return;
    if (!item.isRead) {
      setLoadingId(item.id);
      try {
        await markRead(item.id);
      } catch {
        setLoadingId(null);
        return;
      }
      setLoadingId(null);
    }
    const route = resolveNotificationRoute(item);
    if (route) {
      navigate(route);
      close();
    }
  };

  const handleMarkReadOnly = async (
    e: React.MouseEvent,
    item: NotificationResponseType,
  ) => {
    e.stopPropagation();
    if (item.isRead || loadingId) return;
    setLoadingId(item.id);
    try {
      await markRead(item.id);
    } catch {
      /* error shown inside markRead */
    } finally {
      setLoadingId(null);
    }
  };

  const handleMarkAll = async () => {
    if (markAllLoading || !count) return;
    setMarkAllLoading(true);
    mutateCount(
      (cur: any) => (cur ? { ...cur, data: 0 } : cur),
      { revalidate: false },
    );
    try {
      await notificationApi.markAllRead();
      revalidateAll();
    } catch {
      mutateCount();
      notification.error({
        title: "Lỗi",
        message: "Không thể đánh dấu tất cả đã đọc",
      });
    } finally {
      setMarkAllLoading(false);
    }
  };

  return (
    <Popover
      opened={opened}
      onClose={close}
      position="bottom-end"
      width={380}
      shadow="lg"
      withArrow
      radius="md"
      withinPortal
      closeOnClickOutside={false}
    >
      <Popover.Target>
        <Tooltip label="Thông báo" position="bottom">
          <Indicator
            label={badgeLabel}
            size={badgeLabel ? 18 : 0}
            color="red"
            disabled={!badgeLabel}
          >
            <ActionIcon
              ref={triggerRef}
              variant="subtle"
              color="gray"
              size="lg"
              onClick={toggle}
              aria-label="Thông báo"
            >
              <IconBell size={20} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ maxWidth: "min(380px, 95vw)" }}>
        <div ref={dropdownRef}>
        {/* Header */}
        <Group px="md" py="sm" justify="space-between" wrap="nowrap">
          <Group gap={6} align="center">
            <Text fw={700} size="sm">
              Thông báo
            </Text>
            {count !== undefined && count > 0 && (
              <Badge color="red" size="sm" variant="filled" radius="xl">
                {count > 99 ? "99+" : count}
              </Badge>
            )}
          </Group>
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            leftSection={<IconChecks size={13} />}
            onClick={handleMarkAll}
            loading={markAllLoading}
            disabled={!count}
          >
            Đọc tất cả
          </Button>
        </Group>

        <Divider />

        {/* Tab filter */}
        <Tabs
          value={activeTab}
          onChange={(v) => setActiveTab((v as NotificationTab) ?? "all")}
          styles={{
            tab: {
              fontSize: "var(--mantine-font-size-sm)",
              paddingTop: 8,
              paddingBottom: 8,
            },
            list: { paddingLeft: 8 },
          }}
        >
          <Tabs.List>
            <Tabs.Tab value="all">Tất cả</Tabs.Tab>
            <Tabs.Tab
              value="unread"
              rightSection={
                count ? (
                  <Badge size="xs" color="blue" variant="light" radius="xl">
                    {count > 99 ? "99+" : count}
                  </Badge>
                ) : undefined
              }
            >
              Chưa đọc
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <Divider />

        {/* List — viewportRef dùng làm root cho IntersectionObserver */}
        <ScrollArea h={360} type="auto" viewportRef={viewportRef}>
          {listLoading && <NotificationListSkeleton />}

          {/* Lỗi khi load lần đầu (chưa có data) */}
          {!listLoading && listError && !notifications && (
            <Box p="md">
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                title="Không tải được thông báo"
              >
                <Button
                  variant="subtle"
                  size="xs"
                  mt={4}
                  onClick={() => mutateList()}
                >
                  Thử lại
                </Button>
              </Alert>
            </Box>
          )}

          {!listLoading && !listError && (!notifications || notifications.length === 0) && (
            <NotificationEmptyState activeTab={activeTab} />
          )}

          {notifications && notifications.length > 0 && (
            <Stack gap={0}>
              {notifications.map((item) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  isLoading={loadingId === item.id}
                  onClick={() => handleItemClick(item)}
                  onMarkRead={(e) => handleMarkReadOnly(e, item)}
                />
              ))}
              <div ref={sentinelRef} style={{ height: 1 }} />
            </Stack>
          )}

          {/* Loading more — hiển thị khi đang fetch thêm (đã có data) */}
          {isValidating && notifications !== undefined && notifications.length > 0 && (
            <Loading loaderProps={{ size: "xs" }} />
          )}

          {/* Lỗi load more — giữ nguyên list, báo lỗi ở cuối */}
          {listError && notifications !== undefined && notifications.length > 0 && (
            <Group justify="center" py="xs" gap="xs">
              <Text size="xs" c="red">
                Không tải được thêm thông báo.
              </Text>
              <Button
                variant="subtle"
                size="xs"
                color="red"
                onClick={() => mutateList()}
              >
                Thử lại
              </Button>
            </Group>
          )}
        </ScrollArea>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};
